// ---------------------------------------------------------------------------
// What the Design System screen is allowed to say about this workspace.
//
// The defect this replaces: the screen opened on four tinted cards reading
// `pass` — "cogo authority chunks 27 / 22 kinds", "Ontology coverage 10 proof
// pass", "Reference corpus 74 checked" — plus a ten-row proof-gate table where
// nine rows were green `pass` and every row ended in "Ready". None of it was
// about the person looking at it. All of it came from
// `agentic/fixtures.ts`, a hand-written snapshot of an internal evaluation run
// dated 2026-06-21, frozen in source and rendered unchanged ever since. A
// creator who had never saved a design system still saw nine green passes.
//
// Same defect class as `latestStudioProductionHealth` rendering "Share links
// verified · 2/2 links reachable" on the Share screen of a workspace that had
// shared nothing (fixed in `apps-page.tsx :: summarizeShareHealth`). The three
// properties of that repair are the three properties here:
//
//   (a) the subject is data the screen actually reads — `listDesignSystems()`
//       against this workspace, not a constant;
//   (b) no state gets a success colour: a colour that reads "all good" would
//       put back the claim the counts are careful not to make;
//   (c) the badge carries its own count and its own date, so it cannot be read
//       as a live verdict once it has scrolled away from its caption.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react'
import type { DesignSystem } from '@cogo/kg-repository'
import { useStudioLocale } from '@/i18n/use-studio-locale'
import type { StudioDictionary } from '@/i18n/studio-dictionaries'
import { parseDesignSystemState } from './lib/theme-tokens'
import { readWorkspaceDesignSystems } from './design-system-consumer'

/**
 * The wording this summary is allowed to use. Defaulted to English so the
 * pure function stays callable (and directly assertable) without a provider;
 * the hook below passes the reader's own language through.
 */
export type DesignSystemInventoryCopy =
  StudioDictionary['designSystem']['inventory']

export type DesignSystemInventoryTone =
  /** The read is in flight. Says nothing about the result. */
  | 'reading'
  /** The read could not complete. The only tone that gets a colour. */
  | 'unreadable'
  /** The read completed and this workspace has no design system. */
  | 'none'
  /** The read completed and returned at least one. */
  | 'listed'

export interface DesignSystemInventorySummary {
  tone: DesignSystemInventoryTone
  /** Self-contained: carries the count and the date of the read it describes. */
  badge: string
  detail: string
  count: number
  /** ISO time of the read this summary describes, or null when none completed. */
  readAt: string | null
}

export function formatInventoryTime(iso: string, locale?: string): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const EN_INVENTORY_COPY: DesignSystemInventoryCopy = {
  readingBadge: 'Reading this workspace\u2019s design systems\u2026',
  readingDetail: 'Nothing is claimed until the list comes back.',
  unreadableBadge: 'Could not read this workspace\u2019s design systems',
  unreadableDetail:
    'The list did not load, so this screen cannot say how many design systems '
    + 'this workspace has. The theme editor below still works.',
  noneBadge: 'No design system saved in this workspace{stamp}',
  noneDetail:
    'Nothing has been saved as a design system here yet. Set the axes below '
    + 'and save it to Library to create the first one.',
  listedBadge: '{count} design systems in this workspace{stamp}',
  listedBadgeOne: '{count} design system in this workspace{stamp}',
  listedDetail:
    'This is the list as it came back at the time above. Opening this screen '
    + 'lists them; it does not check anything about them.',
  readStamp: ', read {time}',
  setAsideNote:
    ' {count} more rows belong to projects created from a template and are '
    + 'not workspace design systems.',
  setAsideNoteOne:
    ' {count} more row belongs to projects created from a template and is '
    + 'not a workspace design system.',
}

function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in values ? values[key] : whole,
  )
}

/**
 * Splits one `listDesignSystems()` read into the two things that share the
 * `design_system_presets` table.
 *
 * Traced 2026-08-04 against production (48 rows, one real account): the table
 * has TWO writers putting different data structures in the same `tokens`
 * column —
 *   A. the theme editor's save path → a six-axis snapshot (a design system a
 *      person chose), and
 *   B. `POST /api/projects/from-template` → the template's own
 *      `design-system.json`, a component→Tailwind class map
 *      (`page-container`, `heading-h1`, `button-primary`, …41 keys), stored
 *      as a by-product of creating a project from a template.
 * 45 of the 48 rows were (B), so "48 design systems in this workspace"
 * silently added up two unlike things and no count on the screen meant
 * anything. This screen is the workspace's design systems, so it counts and
 * lists (A) and says how many (B) rows it set aside.
 *
 * The test is `parseDesignSystemState` — the same parser the row's "Load in
 * editor" action gates on — so a row is counted here exactly when it is
 * loadable there. One rule, one place: the number cannot drift from the list.
 * (B) rows are NOT deleted or hidden from their projects; they are simply not
 * this workspace-level surface's subject.
 */
export function partitionDesignSystems(
  systems: readonly DesignSystem[],
): { authored: DesignSystem[]; templateArtifacts: DesignSystem[] } {
  const authored: DesignSystem[] = []
  const templateArtifacts: DesignSystem[] = []
  for (const system of systems) {
    if (parseDesignSystemState(system.axes)) authored.push(system)
    else templateArtifacts.push(system)
  }
  return { authored, templateArtifacts }
}

/**
 * Pure summary of one `listDesignSystems()` read. Exported for tests: the
 * wording is the product here, so it is asserted directly rather than through
 * a rendered tree.
 *
 * `systems` is the AUTHORED subset (see `partitionDesignSystems`);
 * `setAsideCount` is how many template by-product rows the same read
 * contained, so the screen can account for them instead of dropping them
 * without a word.
 */
export function summarizeDesignSystemInventory(input: {
  systems: readonly DesignSystem[] | null
  error: string | null
  readAt: string | null
  setAsideCount?: number
  copy?: DesignSystemInventoryCopy
  locale?: string
}): DesignSystemInventorySummary {
  const {
    systems,
    error,
    readAt,
    setAsideCount = 0,
    copy = EN_INVENTORY_COPY,
    locale,
  } = input
  const setAsideNote =
    setAsideCount > 0
      ? fill(setAsideCount === 1 ? copy.setAsideNoteOne : copy.setAsideNote, {
          count: String(setAsideCount),
        })
      : ''

  if (error) {
    return {
      tone: 'unreadable',
      badge: copy.unreadableBadge,
      detail: copy.unreadableDetail,
      count: 0,
      readAt,
    }
  }

  if (!systems) {
    return {
      tone: 'reading',
      badge: copy.readingBadge,
      detail: copy.readingDetail,
      count: 0,
      readAt: null,
    }
  }

  const count = systems.length
  const stamp = readAt
    ? fill(copy.readStamp, { time: formatInventoryTime(readAt, locale) })
    : ''

  if (count === 0) {
    return {
      tone: 'none',
      badge: fill(copy.noneBadge, { stamp }),
      detail: copy.noneDetail + setAsideNote,
      count,
      readAt,
    }
  }

  return {
    tone: 'listed',
    badge: fill(count === 1 ? copy.listedBadgeOne : copy.listedBadge, {
      count: String(count),
      stamp,
    }),
    detail: copy.listedDetail + setAsideNote,
    count,
    readAt,
  }
}

export interface DesignSystemInventoryState {
  summary: DesignSystemInventorySummary
  systems: readonly DesignSystem[]
  retry: () => void
}

/**
 * Live read of the design systems this workspace can see. Inventory belongs
 * to the authenticated platform boundary, not to the optional local KG
 * bootstrap: DGX therefore reads it through the same-origin Cogo proxy and
 * can retry without a Supabase browser client or a WASM/CDN dependency.
 */
export function useDesignSystemInventory(): DesignSystemInventoryState {
  const { locale, strings } = useStudioLocale()
  const [systems, setSystems] = useState<DesignSystem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [readAt, setReadAt] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  const retry = useCallback(() => {
    setSystems(null)
    setError(null)
    setReadAt(null)
    setAttempt((current) => current + 1)
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false
    void (async () => {
      try {
        const result = await readWorkspaceDesignSystems(controller.signal)
        if (cancelled) return
        setSystems([...result])
        setError(null)
        setReadAt(new Date().toISOString())
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : String(err))
        setReadAt(new Date().toISOString())
      }
    })()
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [attempt])

  // One read, split once: the count and the list below it are derived from
  // the same partition, so they cannot disagree about what a design system is.
  const { authored, templateArtifacts } = partitionDesignSystems(systems ?? [])

  return {
    summary: summarizeDesignSystemInventory({
      systems: systems === null ? null : authored,
      error,
      readAt,
      setAsideCount: templateArtifacts.length,
      copy: strings.designSystem.inventory,
      locale,
    }),
    systems: authored,
    retry,
  }
}
